import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';

/**
 * DateTimeInput - Custom date/time input component
 * Format: MMDDHHMM (8 digits)
 * Example: 04081500 = 04/08/2026 at 15:00
 * 
 * Stores value as ISO datetime string for database compatibility
 */
export const DateTimeInput = ({ 
  value, 
  onChange, 
  className = '', 
  placeholder = 'MMDDHHMM',
  disabled = false,
  'data-testid': testId,
  ...props 
}) => {
  // Convert ISO datetime to display format (MMDD HHMM)
  const isoToDisplay = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${month}${day}${hours}${minutes}`;
    } catch {
      return '';
    }
  };

  // Convert display format to ISO datetime
  const displayToIso = (displayValue) => {
    if (!displayValue || displayValue.length !== 8) return '';
    
    const month = parseInt(displayValue.substring(0, 2), 10);
    const day = parseInt(displayValue.substring(2, 4), 10);
    const hours = parseInt(displayValue.substring(4, 6), 10);
    const minutes = parseInt(displayValue.substring(6, 8), 10);
    
    // Validate ranges
    if (month < 1 || month > 12) return '';
    if (day < 1 || day > 31) return '';
    if (hours < 0 || hours > 23) return '';
    if (minutes < 0 || minutes > 59) return '';
    
    // Use current year
    const year = new Date().getFullYear();
    
    // Create ISO string
    const isoString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    
    return isoString;
  };

  // Format display value with visual separator
  const formatDisplayValue = (raw) => {
    if (!raw) return '';
    // Remove any non-digits
    const digits = raw.replace(/\D/g, '');
    
    if (digits.length <= 4) {
      // MMDD part
      if (digits.length <= 2) return digits;
      return `${digits.substring(0, 2)}/${digits.substring(2)}`;
    } else {
      // MMDD HHMM part
      const mmdd = `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
      const hhmm = digits.substring(4, 8);
      return `${mmdd} ${hhmm}`;
    }
  };

  // Parse display value back to raw digits
  const parseDisplayValue = (formatted) => {
    return formatted.replace(/\D/g, '').substring(0, 8);
  };

  const [displayValue, setDisplayValue] = useState(() => {
    const raw = isoToDisplay(value);
    return formatDisplayValue(raw);
  });

  // Update display when value prop changes
  useEffect(() => {
    const raw = isoToDisplay(value);
    setDisplayValue(formatDisplayValue(raw));
  }, [value]);

  const handleChange = (e) => {
    const input = e.target.value;
    const rawDigits = parseDisplayValue(input);
    
    // Format for display
    const formatted = formatDisplayValue(rawDigits);
    setDisplayValue(formatted);
    
    // If we have 8 digits, convert to ISO and call onChange
    if (rawDigits.length === 8) {
      const isoValue = displayToIso(rawDigits);
      if (isoValue && onChange) {
        onChange({ target: { value: isoValue } });
      }
    } else if (rawDigits.length === 0 && onChange) {
      // Clear the value
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    const rawDigits = parseDisplayValue(displayValue);
    if (rawDigits.length > 0 && rawDigits.length < 8) {
      // If incomplete, clear the field
      setDisplayValue('');
      if (onChange) {
        onChange({ target: { value: '' } });
      }
    }
  };

  return (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={`font-mono ${className}`}
      maxLength={13} // MM/DD HHMM = 13 chars with formatting
      data-testid={testId}
      {...props}
    />
  );
};

/**
 * DateInput - Custom date input component (date only, no time)
 * Format: MMDD (4 digits)
 * Example: 0408 = 04/08/2026
 */
export const DateInput = ({ 
  value, 
  onChange, 
  className = '', 
  placeholder = 'MM/DD',
  disabled = false,
  'data-testid': testId,
  ...props 
}) => {
  // Convert ISO/YYYY-MM-DD date to display format (MM/DD)
  const isoToDisplay = (isoString) => {
    if (!isoString) return '';
    try {
      // Handle both ISO datetime and YYYY-MM-DD formats
      const dateStr = isoString.split('T')[0];
      const parts = dateStr.split('-');
      if (parts.length >= 3) {
        return `${parts[1]}/${parts[2]}`;
      }
      return '';
    } catch {
      return '';
    }
  };

  // Convert display format to YYYY-MM-DD
  const displayToIso = (displayValue) => {
    const digits = displayValue.replace(/\D/g, '');
    if (digits.length !== 4) return '';
    
    const month = parseInt(digits.substring(0, 2), 10);
    const day = parseInt(digits.substring(2, 4), 10);
    
    // Validate ranges
    if (month < 1 || month > 12) return '';
    if (day < 1 || day > 31) return '';
    
    // Use current year
    const year = new Date().getFullYear();
    
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Format display value with slash
  const formatDisplayValue = (raw) => {
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    
    if (digits.length <= 2) return digits;
    return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
  };

  const [displayValue, setDisplayValue] = useState(() => isoToDisplay(value));

  // Update display when value prop changes
  useEffect(() => {
    setDisplayValue(isoToDisplay(value));
  }, [value]);

  const handleChange = (e) => {
    const input = e.target.value;
    const rawDigits = input.replace(/\D/g, '').substring(0, 4);
    
    // Format for display
    const formatted = formatDisplayValue(rawDigits);
    setDisplayValue(formatted);
    
    // If we have 4 digits, convert to ISO and call onChange
    if (rawDigits.length === 4) {
      const isoValue = displayToIso(rawDigits);
      if (isoValue && onChange) {
        onChange({ target: { value: isoValue } });
      }
    } else if (rawDigits.length === 0 && onChange) {
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    const rawDigits = displayValue.replace(/\D/g, '');
    if (rawDigits.length > 0 && rawDigits.length < 4) {
      setDisplayValue('');
      if (onChange) {
        onChange({ target: { value: '' } });
      }
    }
  };

  return (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={`font-mono ${className}`}
      maxLength={5} // MM/DD = 5 chars
      data-testid={testId}
      {...props}
    />
  );
};

/**
 * TimeInput - time only (HHMM → "HH:MM")
 */
export const TimeInput = ({ value, onChange, className = '', placeholder = 'HH:MM', disabled = false, 'data-testid': testId, ...props }) => {
  const toDisplay = (v) => (v ? v.replace(/\D/g, '').substring(0, 4).replace(/^(\d{2})(\d{1,2})$/, '$1:$2') : '');
  const [display, setDisplay] = useState(() => toDisplay(value));
  useEffect(() => { setDisplay(toDisplay(value)); }, [value]);

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').substring(0, 4);
    setDisplay(toDisplay(digits));
    if (digits.length === 4) {
      const h = parseInt(digits.substring(0, 2), 10);
      const m = parseInt(digits.substring(2, 4), 10);
      if (h <= 23 && m <= 59 && onChange) onChange({ target: { value: `${digits.substring(0, 2)}:${digits.substring(2, 4)}` } });
    } else if (digits.length === 0 && onChange) {
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    const digits = display.replace(/\D/g, '');
    if (digits.length > 0 && digits.length < 4) {
      setDisplay('');
      if (onChange) onChange({ target: { value: '' } });
    }
  };

  return (
    <Input
      type="text"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={`font-mono ${className}`}
      maxLength={5}
      data-testid={testId}
      {...props}
    />
  );
};

export default DateTimeInput;
